import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CheckboxComponent } from '@core/components/checkbox/checkbox.component';
import { LoadingIconComponent } from '@core/components/loading-icon/loading-icon.component';
import { SFormFieldComponent } from '@core/components/s-form-field/s-form-field.component';
import { SessionService } from '@core/services/session.service';
import { SocketService } from '@core/services/socket.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { disabled, Field, form, required } from '@angular/forms/signals';
import { AffiliatesService } from '@features/affiliates/services/affiliates.service';
import { ModalService } from '@features/modals/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-affiliates-claim-modal',
  imports: [
    CheckboxComponent,
    LoadingIconComponent,
    SvgIconComponent,
    Field,
    SFormFieldComponent,
    RouterLink,
  ],
  templateUrl: './affiliates-claim-modal.component.html',
  styleUrl: './affiliates-claim-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AffiliatesClaimModalComponent {
  public readonly sessionService = inject(SessionService);
  public readonly affiliatesService = inject(AffiliatesService);
  public readonly socketService = inject(SocketService);
  private readonly modalService = inject(ModalService);
  private readonly toastrService = inject(ToastrService);

  stats = computed(() => this.affiliatesService.basics());
  hasReferral = computed(() => !!this.stats()?.ref_by || !!this.stats()?.ref_next);
  // The code shown in the locked field. `ref_by` is a Steam ID, never a code:
  // a confirmed referral exposes the code that was used as `ref_used`, a pending
  // one as `ref_next_code`. `||` (not `??`) because the BE sends "" for "absent".
  activeCode = computed(() => {
    const s = this.stats();
    if (!s) return '';
    const confirmed = s.ref_by ? s.ref_used : '';
    return String(confirmed || s.ref_next_code || s.ref_used || '');
  });
  basicsUnknown = computed(() => !this.affiliatesService.basicsEverLoaded());
  fieldLocked = computed(() => this.hasReferral() || this.basicsUnknown());

  isLoading = computed(() => this.affiliatesService.isLoading());

  codeModel = signal<{ code: string }>({
    code: '',
  });

  codeForm = form(this.codeModel, (p) => {
    required(p.code, { message: 'Code is required' });
    disabled(p.code, () => this.fieldLocked());
  });

  codeFormApiError = signal<string | null>(null);

  private readonly justClaimed = signal(false);
  coinsClaimed = computed(() => this.justClaimed());
  /** True while a claim ack is pending: blocks a second click in the 5 s window. */
  claiming = signal(false);

  claimError = signal<string | null>(null);

  private wasReferred = false;

  constructor() {
    this.affiliatesService.refreshAll();

    // Prefill/clear the code field on a referred <-> not-referred transition (AC-9).
    // Tracks hasReferral(), not fieldLocked(): the unknown-state lock must not prefill.
    effect(() => {
      const referred = this.hasReferral();
      const code = this.activeCode();
      untracked(() => {
        if (referred) this.codeModel.set({ code });
        else if (this.wasReferred) this.codeModel.set({ code: '' });
        this.wasReferred = referred;
      });
    });

    // BE says the coin is available again (e.g. /resetbonus + Refresh) -> restore the button.
    effect(() => {
      if (this.affiliatesService.bonuses()?.balance) {
        untracked(() => this.justClaimed.set(false));
      }
    });
  }

  public async handleClaimAffiliateCode(event: Event) {
    event.preventDefault();
    // mirror the template gate; the BE remains the real authority
    if (this.fieldLocked()) return;
    this.codeFormApiError.set(null);
    try {
      const message = await this.affiliatesService.redeemReferralCode(
        this.codeForm().value().code
      );

      if (message) {
        this.toastrService.success(message);
        this.modalService.close();
      }
    } catch (error: any) {
      // a timeout rejects with an Error object; never leak its wording into the UI
      this.codeFormApiError.set(
        typeof error === 'string' && error ? error : 'Something went wrong'
      );
    }
  }

  public async refreshBalance() {
    this.claimError.set(null);
    this.affiliatesService.refreshAll();
    const balanceType = this.sessionService.balanceType();
    try {
      const res = await this.affiliatesService.refreshBalance(balanceType);
      this.sessionService.setBalance(balanceType, res);
    } catch {
      // toast already shown by the socket service
    }
  }

  public async claimBalance() {
    if (this.claiming()) return;
    this.claiming.set(true);
    this.claimError.set(null);
    try {
      const res = await this.affiliatesService.claimBalance();
      // set only after the BE confirms so animate.leave never fires on a rejection
      this.justClaimed.set(true);
      this.toastrService.success(`You successfully claimed ${res} coins`);
      this.sessionService.changeBalanceType('bonus');
    } catch (error: any) {
      this.justClaimed.set(false);
      this.claimError.set(
        typeof error === 'string' && error ? error : 'Claim failed'
      );
    } finally {
      this.claiming.set(false);
    }
  }

  public closeModal() {
    this.modalService.close();
  }
}
